import * as React from "react";
import { authService } from "@/services/authService";
import type { User } from "@/services/authService";
import { checkKTPStatus } from "@/utils/ktpValidation";
import { KTPAlertDialog } from "@/components/dashboard/components/KTPAlertDialog";
import type { KTPAlertType } from "@/components/dashboard/components/KTPAlertDialog";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [ktpAlertOpen, setKtpAlertOpen] = React.useState(false);
  const [ktpAlertType, setKtpAlertType] = React.useState<KTPAlertType | null>(null);

  // Check authentication status on mount
  React.useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);

      try {
        const currentUser = await authService.getCurrentUser();

        if (currentUser) {
          setUser(currentUser);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        setUser(null);
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = React.useCallback(async (email: string, password: string) => {
    try {
      await authService.login({ email, password });
      // Fetch full user (login response has minimal data)
      const currentUser = await authService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
      }
    } catch (error) {
      throw error;
    }
  }, []);

  const logout = React.useCallback(async () => {
    try {
      // Always clear user state first to ensure UI updates immediately
      setUser(null);

      // Then attempt to logout on server
      await authService.logout();
    } catch (error) {
      console.error("Logout error:", error);
      // Ensure user state is cleared even if logout API fails
      setUser(null);
      // Re-throw error so UI can handle it
      throw error;
    }
  }, []);

  const refreshUser = React.useCallback(async (): Promise<User | null> => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      return currentUser;
    } catch (error) {
      console.error("Error refreshing user:", error);
      setUser(null);
      return null;
    }
  }, []);

  // Re-sync hak akses / profile when returning to the tab (assignments may have changed server-side)
  React.useEffect(() => {
    if (!user) return;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        void refreshUser();
      }, 400);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, refreshUser]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {ktpAlertType && (
        <KTPAlertDialog
          open={ktpAlertOpen}
          onOpenChange={setKtpAlertOpen}
          type={ktpAlertType}
        />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

